import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Users, Shield, Building2, Trash2, KeyRound, CheckCircle2, Mail, LogOut } from 'lucide-react';
import { toast } from 'sonner';

export default function UsersManagement() {
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'hr',
    company_id: '',
    allowed_company_ids: []
  });
  const [editingScope, setEditingScope] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);      // utilisateur à réinitialiser
  const [resetResult, setResetResult] = useState(null);      // { user_email, full_name }
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/create', formData);
      toast.success('Utilisateur créé avec succès');
      setOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'hr',
      company_id: '',
      allowed_company_ids: []
    });
  };

  const roleLabels = {
    super_admin: 'Super Administrateur',
    company_admin: 'Administrateur Entreprise',
    hr: 'Ressources Humaines',
    director: 'Directeur',
    group_admin: 'Groupe - Administrateur',
    group_hr: 'Groupe - RH'
  };

  const saveScope = async () => {
    if (!editingScope) return;
    try {
      await api.patch(`/users/${editingScope.userId}/companies`, {
        allowed_company_ids: editingScope.allowed
      });
      toast.success('Périmètre mis à jour');
      setEditingScope(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur mise à jour');
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'N/A';
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(
      `Supprimer l'utilisateur "${u.email}" ?\n\n` +
      `⚠️ ATTENTION : Cette action va aussi supprimer l'EMPLOYÉ associé et tous ses accès.\n\n` +
      `Cette opération est irréversible. Continuer ?`
    )) return;
    try {
      const res = await api.delete(`/users/${u.id}`);
      if (res.data?.employee_deleted) {
        toast.success(`Utilisateur ${u.email} supprimé (compte + employé)`);
      } else {
        toast.success(`Utilisateur ${u.email} supprimé (aucun employé associé)`);
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await api.post(`/users/${resetTarget.id}/reset-password`);
      setResetTarget(null);
      setResetResult({
        user_email: res.data.user_email,
        full_name: resetTarget.full_name,
      });
      toast.success(`Mot de passe réinitialisé — email envoyé à ${res.data.user_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  const handleForceLogout = async (u) => {
    if (!window.confirm(`Déconnecter de force ${u.full_name} ? Tous ses tokens seront immédiatement révoqués.`)) return;
    try {
      await api.post(`/users/${u.id}/force-logout`);
      toast.success(`${u.full_name} a été déconnecté de force. Tous ses tokens sont révoqués.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du force-logout');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  // Filtrage par entreprise sélectionnée dans le sélecteur global
  const filteredUsers = isCrossCompany && selectedCompanyId
    ? users.filter(u => u.company_id === selectedCompanyId)
    : users;

  return (
    <div className="space-y-6" data-testid="users-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Gestion des Utilisateurs</h1>
          <p className="text-slate-600 mt-1">Créer et gérer les accès administrateurs</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-700" data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Créer Utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Créer un Utilisateur</DialogTitle>
              <DialogDescription>
                Créez un nouvel utilisateur et assignez-lui un rôle
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Nom complet *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="fullname-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  data-testid="password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Rôle *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company_admin">Administrateur Entreprise</SelectItem>
                    <SelectItem value="hr">Ressources Humaines</SelectItem>
                    <SelectItem value="director">Directeur</SelectItem>
                    <SelectItem value="group_admin">Groupe - Administrateur (multi-entreprises)</SelectItem>
                    <SelectItem value="group_hr">Groupe - RH (multi-entreprises)</SelectItem>
                    <SelectItem value="super_admin">Super Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.role !== 'super_admin' && (
                <div className="space-y-2">
                  <Label>
                    {['group_admin', 'group_hr'].includes(formData.role)
                      ? 'Entreprises accessibles * (plusieurs possibles)'
                      : 'Entreprise *'}
                  </Label>
                  {['group_admin', 'group_hr'].includes(formData.role) ? (
                    <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 bg-slate-50" data-testid="multi-company-select">
                      {companies.map((c) => {
                        const checked = (formData.allowed_company_ids || []).includes(c.id);
                        return (
                          <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const current = formData.allowed_company_ids || [];
                                setFormData({
                                  ...formData,
                                  allowed_company_ids: e.target.checked
                                    ? [...current, c.id]
                                    : current.filter((x) => x !== c.id),
                                });
                              }}
                              data-testid={`user-scope-${c.id}`}
                            />
                            <span className="text-sm">{c.name}</span>
                          </label>
                        );
                      })}
                      {companies.length === 0 && (
                        <p className="text-xs text-slate-500">Aucune entreprise disponible</p>
                      )}
                    </div>
                  ) : (
                    <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                      <SelectTrigger data-testid="company-select">
                        <SelectValue placeholder="Sélectionner une entreprise" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary" data-testid="submit-user-button">
                  Créer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{users.length}</div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Super Admins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {users.filter(u => u.role === 'super_admin').length}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Admins Entreprise</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-success">
              {users.filter(u => u.role === 'company_admin').length}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">RH & Directeurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-700">
              {users.filter(u => u.role === 'hr' || u.role === 'director').length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Liste des Utilisateurs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Nom</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Rôle</TableHead>
                  <TableHead className="font-semibold">Entreprise(s)</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                      Aucun utilisateur trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const isGroup = ['group_admin', 'group_hr'].includes(user.role);
                    const allowed = user.allowed_company_ids || [];
                    return (
                      <TableRow key={user.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-slate-400" />
                            {roleLabels[user.role] || user.role}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isGroup ? (
                            allowed.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {allowed.slice(0, 3).map((cid) => (
                                  <Badge key={cid} variant="outline" className="text-xs">{getCompanyName(cid)}</Badge>
                                ))}
                                {allowed.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{allowed.length - 3}</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-500 italic text-xs">Toutes (aucune restriction)</span>
                            )
                          ) : user.company_id ? (
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              {getCompanyName(user.company_id)}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={user.is_active ? 'bg-success text-white' : 'bg-error text-white'}>
                            {user.is_active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isGroup && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingScope({ userId: user.id, allowed: [...allowed], email: user.email })}
                                data-testid={`edit-scope-${user.id}`}
                              >
                                <Building2 className="w-3 h-3 mr-1" />
                                Entreprises
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 hover:bg-amber-50 hover:text-amber-700 border-amber-200"
                              onClick={() => setResetTarget(user)}
                              title="Réinitialiser le mot de passe"
                              data-testid={`reset-pwd-${user.id}`}
                            >
                              <KeyRound className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-orange-600 hover:bg-orange-50 hover:text-orange-700 border-orange-200"
                              onClick={() => handleForceLogout(user)}
                              title="Déconnecter de force (révoque tous les tokens actifs)"
                              data-testid={`force-logout-${user.id}`}
                            >
                              <LogOut className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                              onClick={() => handleDeleteUser(user)}
                              title="Supprimer cet utilisateur et l'employé associé"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editingScope} onOpenChange={(v) => !v && setEditingScope(null)}>
        <DialogContent className="max-w-md" data-testid="edit-scope-dialog">
          <DialogHeader>
            <DialogTitle>Périmètre des entreprises</DialogTitle>
            <DialogDescription>
              {editingScope?.email} — cochez les entreprises accessibles.
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-md p-3 max-h-72 overflow-y-auto space-y-2 bg-slate-50">
            {companies.map((c) => {
              const checked = editingScope?.allowed?.includes(c.id);
              return (
                <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                  <input
                    type="checkbox"
                    checked={checked || false}
                    onChange={(e) => {
                      const current = editingScope.allowed || [];
                      setEditingScope({
                        ...editingScope,
                        allowed: e.target.checked
                          ? [...current, c.id]
                          : current.filter((x) => x !== c.id),
                      });
                    }}
                    data-testid={`scope-cb-${c.id}`}
                  />
                  <span className="text-sm">{c.name}</span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-slate-500">
            Laissez vide pour permettre l&apos;accès à <strong>toutes</strong> les entreprises.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setEditingScope(null)}>
              Annuler
            </Button>
            <Button onClick={saveScope} data-testid="save-scope">
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* ── Modale confirmation réinitialisation mot de passe ── */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <KeyRound className="w-5 h-5" />
              Réinitialiser le mot de passe
            </DialogTitle>
            <DialogDescription>
              Un nouveau mot de passe temporaire sera généré et envoyé automatiquement par email à l'utilisateur.
            </DialogDescription>
          </DialogHeader>
          {resetTarget && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium text-slate-800">{resetTarget.full_name}</p>
                <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                  <Mail className="w-3 h-3" />
                  {resetTarget.email}
                </p>
              </div>
              <p className="text-sm text-slate-600">
                L'utilisateur devra changer ce mot de passe à sa prochaine connexion.
              </p>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setResetTarget(null)}>
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleResetPassword}
                  disabled={resetting}
                  data-testid="confirm-reset-btn"
                >
                  {resetting ? 'Réinitialisation...' : 'Confirmer la réinitialisation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Modale résultat : confirmation de réinitialisation ── */}
      <Dialog open={!!resetResult} onOpenChange={(open) => !open && setResetResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              Mot de passe réinitialisé avec succès
            </DialogTitle>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 flex items-center gap-2 font-medium">
                  <Mail className="w-4 h-4 shrink-0" />
                  Email envoyé à <strong>{resetResult.user_email}</strong>
                </p>
                <p className="text-xs text-green-700 mt-2">
                  Le nouveau mot de passe temporaire a été transmis uniquement par email à l'utilisateur.
                  Il sera invité à le changer dès sa prochaine connexion.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-500">
                  🔒 Pour des raisons de sécurité, le mot de passe temporaire n'est
                  pas affiché ici. Il est transmis exclusivement par email à l'utilisateur.
                </p>
              </div>

              <Button className="w-full" onClick={() => setResetResult(null)}>
                Fermer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
